import { CheckCircle2, Circle, FileText, BookOpen, Send, Award, ShieldCheck, GraduationCap, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  { key: "pending", label: "Application Submitted", description: "Your application has been received", icon: Upload },
  { key: "document_verified", label: "Documents Verified", description: "All documents have been reviewed", icon: FileText },
  { key: "course_assigned", label: "Course Assigned", description: "Your course materials are ready", icon: BookOpen },
  { key: "eligible_for_exam", label: "Eligible for Exam", description: "Complete your assigned exam(s) to proceed", icon: Award },
  { key: "sent_for_approval", label: "Sent for Approval", description: "Under review by ACA committee", icon: Send },
  { key: "approved", label: "ACA Approved", description: "Congratulations on your approval!", icon: ShieldCheck },
  { key: "certified", label: "Certified", description: "You are now ACA certified!", icon: GraduationCap },
];

const STATUS_ORDER = STEPS.map((s) => s.key);

// Statuses that aren't their own timeline step map onto an existing one:
// "paid" shows as "certified"; "exception" (skip-exam path) sits at the exam stage.
const normalizeStatus = (status: string) =>
  status === "paid" ? "certified" : status === "exception" ? "eligible_for_exam" : status;

export const getProgressPercent = (status: string) => {
  const effective = normalizeStatus(status);
  const idx = STATUS_ORDER.indexOf(effective);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / STEPS.length) * 100);
};

// Statuses that represent a completed milestone (current step shown as green checkmark)
const MILESTONE_STATUSES = new Set(["document_verified", "course_assigned", "eligible_for_exam", "approved", "certified", "paid"]);

const StatusTimeline = ({ currentStatus }: { currentStatus: string }) => {
  const effectiveStatus = normalizeStatus(currentStatus);
  const currentIdx = STATUS_ORDER.indexOf(effectiveStatus);
  const isRejected = currentStatus === "rejected";
  const isMilestone = MILESTONE_STATUSES.has(effectiveStatus);

  return (
    <div className="space-y-0">
      {STEPS.map((step, i) => {
        const isCompleted = !isRejected && (i < currentIdx || (i === currentIdx && isMilestone));
        const isCurrent = !isRejected && i === currentIdx && !isMilestone;
        const isFuture = isRejected || i > currentIdx;
        const Icon = step.icon;

        return (
          <div
            key={step.key}
            className={cn(
              "flex items-start gap-4 transition-all duration-300",
              isFuture && "opacity-40"
            )}
          >
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300",
                  isCompleted && "bg-success text-success-foreground shadow-md",
                  isCurrent && "bg-primary text-primary-foreground glow-ring",
                  isFuture && "bg-muted text-muted-foreground"
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : isCurrent ? (
                  <Icon className="h-4 w-4 animate-pulse" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    "w-0.5 h-8 my-1 transition-colors duration-300",
                    isCompleted ? "bg-success" : "bg-border"
                  )}
                />
              )}
            </div>
            <div className="pt-1.5 min-w-0">
              <p
                className={cn(
                  "text-sm font-medium leading-tight",
                  isCompleted && "text-success",
                  isCurrent && "text-foreground font-semibold",
                  isFuture && "text-muted-foreground"
                )}
              >
                {step.label}
              </p>
              <p
                className={cn(
                  "text-xs mt-0.5 leading-snug",
                  isCurrent ? "text-muted-foreground" : "text-muted-foreground/60"
                )}
              >
                {step.description}
              </p>
            </div>
          </div>
        );
      })}
      {isRejected && (
        <div className="flex items-start gap-4 mt-2">
          <div className="flex flex-col items-center">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-destructive text-destructive-foreground shadow-md">
              <Circle className="h-5 w-5" />
            </div>
          </div>
          <div className="pt-1.5">
            <p className="text-sm font-semibold text-destructive">Application Rejected</p>
            <p className="text-xs text-destructive/70 mt-0.5">Please contact support for next steps</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusTimeline;
